import React, { useEffect, useMemo, useRef, useState } from "react";
import { translateUi } from './i18n/index';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { ChevronDown } from "./icons";
import { gradeOptionPillClasses } from "./assessmentCriteriaGates";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type AssessmentPillOption = {
  value: string;
  label: string;
};

type AssessmentPillSelectProps = {
  value: string;
  options: AssessmentPillOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When true, colour options/trigger as grade outcome pills. */
  colourByGrade?: boolean;
  isDarkMode?: boolean;
  className?: string;
  "aria-label"?: string;
};

/**
 * Custom select used in assessments so options can render as pills (native <option> cannot).
 * Options should already be in display order (alphabetical where required).
 */
export function AssessmentPillSelect({
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select…",
  colourByGrade = false,
  isDarkMode = false,
  className,
  "aria-label": ariaLabel,
}: AssessmentPillSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);
  const displayLabel = selected?.label || placeholder;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const triggerShell = isDarkMode
    ? "bg-slate-900 text-slate-100 border border-slate-600"
    : "bg-white text-slate-800 border border-slate-200";

  const triggerLabel = (() => {
    if (colourByGrade && value) {
      return cn(
        "inline-flex max-w-[calc(100%-1.75rem)] items-center truncate rounded-full px-3 py-1.5 text-sm font-medium",
        gradeOptionPillClasses(value, isDarkMode)
      );
    }
    return cn(
      "truncate text-sm",
      value
        ? isDarkMode
          ? "text-slate-100"
          : "text-slate-800"
        : isDarkMode
          ? "text-slate-400"
          : "text-slate-500"
    );
  })();

  return (
    <div ref={rootRef} className={cn("relative w-full", className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full min-h-[48px] px-3 py-2 rounded-xl text-base text-left flex items-center justify-between gap-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none disabled:opacity-60",
          triggerShell
        )}
      >
        <span className={triggerLabel}>{displayLabel}</span>
        <ChevronDown className={cn("w-4 h-4 shrink-0 transition-transform", open && "rotate-180", isDarkMode ? "text-slate-400" : "text-slate-500")} />
      </button>
      {open && (
        <ul
          role="listbox"
          className={cn(
            "absolute z-40 mt-1.5 w-full max-h-64 overflow-y-auto rounded-xl border shadow-lg",
            colourByGrade ? "p-2 space-y-1.5" : "py-1",
            isDarkMode ? "bg-slate-900 border-slate-600" : "bg-white border-slate-200"
          )}
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            if (colourByGrade) {
              const pill = gradeOptionPillClasses(opt.value || null, isDarkMode);
              const emptyPill = isDarkMode
                ? "bg-transparent text-slate-400 border border-dashed border-slate-600"
                : "bg-transparent text-slate-500 border border-dashed border-slate-300";
              return (
                <li key={opt.value || "__empty__"} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      "w-full text-left rounded-lg px-1.5 py-1 hover:bg-indigo-500/10 focus:bg-indigo-500/10 focus:outline-none",
                      isSelected && (isDarkMode ? "bg-indigo-500/20" : "bg-indigo-50")
                    )}
                  >
                    <span
                      className={cn(
                        "inline-flex max-w-full items-center truncate rounded-full px-3 py-1.5 text-sm font-medium",
                        opt.value ? pill : emptyPill
                      )}
                    >
                      {opt.label}
                    </span>
                  </button>
                </li>
              );
            }
            return (
              <li key={opt.value || "__empty__"} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-3 py-2.5 text-sm focus:outline-none",
                    isDarkMode
                      ? "text-slate-100 hover:bg-slate-800 focus:bg-slate-800"
                      : "text-slate-800 hover:bg-slate-50 focus:bg-slate-50",
                    isSelected && (isDarkMode ? "bg-indigo-500/20 text-indigo-200" : "bg-indigo-50 text-indigo-800"),
                    !opt.value && (isDarkMode ? "text-slate-400" : "text-slate-500")
                  )}
                >
                  {opt.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Sort assessment method option values by display label (A–Z). */
export function sortAssessmentMethodsAlphabetical(
  methods: readonly string[],
  toLabel: (value: string) => string
): string[] {
  return [...methods].sort((a, b) =>
    toLabel(a).localeCompare(toLabel(b), undefined, { sensitivity: "base" })
  );
}

/** Sort grade names A–Z (case-insensitive). */
export function sortGradesAlphabetical(grades: readonly string[]): string[] {
  return [...grades].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** Build grade picker options: placeholder first, then A–Z grades, then Not applicable last. */
export function buildAlphabeticalGradeOptions(
  gradeNames: readonly string[],
  opts?: { includeNotApplicable?: boolean; placeholderLabel?: string }
): AssessmentPillOption[] {
  const includeNa = opts?.includeNotApplicable !== false;
  const placeholder = opts?.placeholderLabel ?? translateUi("Not yet Assessed");
  const sorted = sortGradesAlphabetical(gradeNames.filter((n) => n.trim() && n.trim().toLowerCase() !== "not applicable"));
  const out: AssessmentPillOption[] = [{ value: "", label: placeholder }, ...sorted.map((name) => ({ value: name, label: name }))];
  if (includeNa) out.push({ value: "Not applicable", label: translateUi("Not applicable") });
  return out;
}
